import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef
} from 'react';
import { useDebounceCallback } from '@elfsight/widget-ui';
import { noop } from 'es-toolkit';
import { initialState, videoAutoplayReducer } from './reducer';
import type { VideoAutoplayContextType, VideoMeta } from './types';
import {
  getVisibleVideos,
  MAX_PLAYING_VIDEOS,
  selectVideosToPlay
} from './utils';

const VideoAutoplayContext = createContext<VideoAutoplayContextType>({
  register: noop,
  unregister: noop,
  onVisibilityChange: noop,
  onHover: noop,
  onHoverEnd: noop,
  recomputePlaying: noop
});

export function VideoAutoplayProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(videoAutoplayReducer, initialState);

  const stateRef = useRef(state);
  stateRef.current = state;

  const register = useCallback((meta: VideoMeta) => {
    dispatch({ type: 'REGISTER', meta });
  }, []);

  const unregister = useCallback((id: string) => {
    const meta = stateRef.current.videos.get(id);
    meta?.ref?.pause();
    dispatch({ type: 'UNREGISTER', id });
  }, []);

  const recomputePlaying = useCallback(() => {
    const { videos, chosenVideoIds } = stateRef.current;
    const list = Array.from(videos.values());

    const someInPopup = list.some((v) => v.isInPopup);
    const visible = getVisibleVideos(videos);

    if (visible.length === 0 || someInPopup) {
      list.forEach((v) => {
        if (!v.isInPopup) {
          v.ref?.pause();
        }
      });

      return;
    }

    const newChosenIds = selectVideosToPlay(visible, chosenVideoIds, videos);

    dispatch({ type: 'UPDATE_CHOSEN_VIDEOS', ids: newChosenIds });

    list.forEach((v) => {
      if (newChosenIds.has(v.id)) {
        v.ref?.play();
      } else {
        v.ref?.pause();

        if (!v.wasCalculated) {
          dispatch({ type: 'MARK_AS_CALCULATED', id: v.id });
        }
      }
    });
  }, []);

  const onHover = useCallback((id: string) => {
    const hoveredVideo = stateRef.current.videos.get(id);

    if (!hoveredVideo) {
      return;
    }

    dispatch({ type: 'SET_HOVERED', id });

    const newChosenIds = new Set(stateRef.current.chosenVideoIds);

    // Если превышен лимит, останавливаем самое дальнее
    if (newChosenIds.size >= MAX_PLAYING_VIDEOS) {
      const playingVideos = Array.from(newChosenIds)
        .map((videoId) => stateRef.current.videos.get(videoId))
        .filter((v): v is VideoMeta => v !== undefined);

      // Находим самое дальнее видео по индексу от hoveredVideo
      const farthest = playingVideos.reduce<VideoMeta | null>((acc, v) => {
        const distance = Math.abs(v.index - hoveredVideo.index);
        const accDistance = acc ? Math.abs(acc.index - hoveredVideo.index) : -1;

        return distance > accDistance ? v : acc;
      }, null);

      if (farthest) {
        newChosenIds.delete(farthest.id);
        farthest.ref?.pause();
      }
    }

    newChosenIds.add(id);
    dispatch({ type: 'UPDATE_CHOSEN_VIDEOS', ids: newChosenIds });

    hoveredVideo.ref?.play();
  }, []);

  const onHoverEnd = useCallback((_id: string) => {
    // При убирании ховера видео продолжает играть
    dispatch({ type: 'SET_HOVERED', id: null });
  }, []);

  const debouncedRecomputePlaying = useDebounceCallback(recomputePlaying);

  const onVisibilityChange = useCallback(
    (id: string, ratio: number) => {
      dispatch({ type: 'UPDATE_VISIBILITY', id, visibility: ratio });
      debouncedRecomputePlaying();
    },
    [debouncedRecomputePlaying]
  );

  const value = useMemo(
    () => ({
      register,
      unregister,
      onVisibilityChange,
      onHover,
      onHoverEnd,
      recomputePlaying: debouncedRecomputePlaying
    }),
    [
      debouncedRecomputePlaying,
      register,
      unregister,
      onVisibilityChange,
      onHover,
      onHoverEnd
    ]
  );

  return (
    <VideoAutoplayContext.Provider value={value}>
      {children}
    </VideoAutoplayContext.Provider>
  );
}

export function useVideoAutoplay() {
  const context = useContext(VideoAutoplayContext);

  if (!context) {
    throw new Error(
      'useVideoAutoplay must be used within a VideoAutoplayProvider'
    );
  }

  return context;
}
